import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clubUsdtcon from '../../../assets/icons/backOffice/CLUBUSDT.svg';
import usdtIcon from '../../../assets/icons/backOffice/USDT.svg';
import mlp from '../../../assets/icons/backOffice/MLP.svg';
import { InputBackOffice } from '../../../components/common/InputBackoffice';
import { CLUB, MLP, USDT, formatUSD } from '../../../helpers/helperFunctions';
import { P2 } from '../../../styles/FontStyles';

import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../app/hooks';
import { ErrorToast } from '../../../components';
import Spinner from '../../../components/common/Spinner';
import { Box, Button } from '../../../styles/GlobalComponents';
import { FlexBetween, FlexCenter, Label } from '../../../styles/stylesStyled';
import { SmallTxt, Subtitle, Title } from '../Withdraw/WithdrawStyled';
import {
  exchangeUsdtToClub,
  exchangeUsdtToMLP,
  exchangeClubToMLP
} from './TopUp.actions';
import { SelectFromFunds, SelectToFunds } from './SelectFunds/SelectFunds';

interface TopUpProps {
  setTopUp: Dispatch<SetStateAction<boolean>>;
}

export const TopUp = ({ setTopUp }: TopUpProps) => {
  const { t } = useTranslation('earnings');
  const [amount, setAmount] = useState('');
  const { info, loading } = useAppSelector((state) => state.earnings);
  const dispatch = useDispatch();

  const digit = /^(?=.*?[0-9])/;

  const [selectedFund, setSelectedFund] = useState<string>(USDT);

  const [selectedReceived, setSelectedReceived] = useState<string>(CLUB);

  const transferFunds = () => {
    if (selectedFund === USDT && selectedReceived === CLUB) {
      dispatch(
        exchangeUsdtToClub({ setAmount, setTopUp, amount: Number(amount) })
      );
    } else if (selectedFund === USDT && selectedReceived === MLP) {
      dispatch(
        exchangeUsdtToMLP({ setAmount, setTopUp, amount: Number(amount) })
      );
    } else if (selectedFund === CLUB && selectedReceived === MLP) {
      dispatch(
        exchangeClubToMLP({ setAmount, setTopUp, amount: Number(amount) })
      );
    }
  };

  return (
    <>
      {loading && <Spinner absolute transparent />}
      <div style={{ marginTop: '8vh' }}>
        <FlexCenter style={{ flexDirection: 'column' }}>
          <Title>{t('top_up_title')}</Title>

          <Subtitle style={{ textAlign: 'center' }}>
            {t('top_up_subtitle')}
          </Subtitle>

          <Label style={{ width: '100%' }}>From</Label>
          <SelectFromFunds
            setSelectedFund={setSelectedFund}
            selectedFund={selectedFund}
            setSelectedReceived={setSelectedReceived}
          />

          <Label style={{ width: '100%' }}>{t('transfer')}</Label>
          <InputBackOffice
            placeholder={t('type_in_amount_to_transfer')}
            icon={selectedFund === USDT ? usdtIcon : clubUsdtcon}
            style={{ marginBottom: 8 }}
            onChange={(e: string) => {
              setAmount(e.replace(/[.,\s\D]/g, ''));
            }}
            pattern='[0-9]'
            value={amount.replace(/[.,\s\D]/g, '')}
          />

          <FlexBetween style={{ width: '100%', marginBottom: 12 }}>
            <SmallTxt>
              Current {selectedFund === USDT ? USDT : 'Club'} balance:{' '}
              {selectedFund === USDT
                ? formatUSD(info?.totalEarnings?.availableBalance)
                : formatUSD(info?.totalEarnings?.shoppingPoints)}{' '}
              {selectedFund === USDT ? USDT : CLUB}
            </SmallTxt>
          </FlexBetween>

          <Label style={{ width: '100%' }}>To</Label>
          <SelectToFunds
            setSelectedFund={setSelectedFund}
            setSelectedReceived={setSelectedReceived}
            selectedReceived={selectedReceived}
          />

          <Label style={{ width: '100%' }}>{t('received')}</Label>
          <Box blue mb='8' p='13px 20px' br='30px'>
            <FlexBetween>
              <P2> {formatUSD(Number(amount))}</P2>
              <img src={selectedReceived === CLUB ? clubUsdtcon : mlp} />
            </FlexBetween>
          </Box>
          <FlexBetween style={{ width: '100%', marginBottom: 12 }}>
            <SmallTxt>
              Current {selectedReceived === CLUB ? 'Club' : 'My Life'} balance:{' '}
              {selectedReceived === CLUB
                ? formatUSD(info?.totalEarnings?.shoppingPoints)
                : formatUSD(info?.totalEarnings?.myLifePoints)}{' '}
              {selectedReceived === CLUB ? CLUB : 'MLP'}
            </SmallTxt>
          </FlexBetween>

          <Button
            maxWidth='200px'
            mt='20'
            disabled={!amount}
            onClick={() => {
              if (!amount || Number(amount) < 1) {
                ErrorToast(t('withdraw_part.initial.err_amount_required'));
              } else if (!amount.match(digit)) {
                ErrorToast(t('withdraw_part.initial.err_number_required'));
              } else if (
                Number(amount) > info!.totalEarnings?.availableBalance
              ) {
                ErrorToast(
                  t('withdraw_part.initial.err_no_enough_usdt', { wULX: USDT })
                );
              } else {
                transferFunds();
              }
            }}>
            {t('transfer')}
          </Button>
        </FlexCenter>
      </div>
    </>
  );
};
